import { cn } from '@ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef } from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        link: cn(
          'text-primary-700 hover:text-primary-800 hover:underline',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
          'transition-colors duration-200'
        ),
        primary: cn(
          'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
          'bg-primary-600 text-white border border-transparent',
          'hover:bg-primary-800 focus:outline-none focus:ring-2 cursor-pointer',
          'focus:ring-primary-800 focus:ring-offset-2',
          'transition-colors duration-200'
        ),
        secondary: cn(
          'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
          'bg-secondary-600 text-white border border-transparent',
          'hover:bg-secondary-800 focus:outline-none focus:ring-2 cursor-pointer',
          'focus:ring-secondary-800 focus:ring-offset-2',
          'transition-colors duration-200'
        ),
        danger: 'bg-red-600 text-white hover:bg-red-800 focus-visible:ring-red-600 cursor-pointer',
        ghost: 'hover:bg-gray-100 text-gray-700 hover:text-gray-900 focus-visible:ring-gray-500 cursor-pointer',
        reverse: cn(
          'inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg',
          'bg-primary-400 text-white border border-primary-600 cursor-pointer ',
          'hover:bg-primary-800 focus:outline-none focus:ring-2',
          'focus:ring-primary-800 focus:ring-offset-2',
          'transition-colors duration-200'
        ),
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-12 px-6 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  icon?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, icon, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {icon && !loading && (icon)}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
